import { Component } from "@theme/component";
import {
  ThemeEvents,
  CartAddInitiatedEvent,
  QuickAddPopupOpenedEvent,
  QuickAddVariantUpdatedEvent,
} from "@theme/events";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import {
  mediaQueryLarge,
  dynamicallyLoadScript,
  requestIdleCallback,
} from "@theme/utilities";
import { morph } from "@theme/morph-dom";

// Matches the desktop quick-add modal breakpoint in async.css (`width > 48rem`).
// Guarded so importing this module in non-browser/test envs (jsdom has no
// matchMedia) is safe; a null value is treated as "not desktop".
const QUICK_ADD_DESKTOP_MEDIA =
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(width > 48rem)")
    : null;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (
      this.closest("product-card-component")?.querySelector(
        'a[ref="productCardLink"]',
      )
    )?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener("change", this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        "text/html",
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute("stay-visible", true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute("stay-visible", false),
      {
        once: true,
      },
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // Remove the inner HTML content on dialog close to ensure variant preloading is triggered on next product dialog open
    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        setTimeout(() => {
          const contentNode = document.getElementById(
            "quick-add-modal-content",
          );
          if (contentNode) contentNode.innerHTML = "";
        }, 250);
      },
      {
        once: true,
      },
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Warms the scripts that upgrade the variant-picker and quantity-selector web
   * components. Called from the product card's prefetch (hover on desktop,
   * in-view on mobile) so the components are already defined when the fetched
   * content is morphed in — they then upgrade on insertion instead of a few
   * seconds after the dialog opens, which used to reflow the quantity/ATC row
   * from stacked to side-by-side (PSD-217 QA). Deduped by dynamicallyLoadScript.
   */
  preloadUpgradeScripts() {
    const sources = window.Theme?.assetSources;
    if (!sources) return;
    dynamicallyLoadScript(sources.variantPickerJs);
    dynamicallyLoadScript(sources.quantitySelectorJs);
  }

  /**
   * Assembles the quick-add content and renders it into the dialog.
   *
   * Both breakpoints render the same allowlisted product-details — the modal
   * (desktop) vs. bottom-drawer (mobile) chrome is the only difference. The
   * product image gallery is stripped here, not CSS-hidden, so the
   * media-gallery script no longer needs loading and the removal is
   * observable in the unit test.
   * See docs/adr/0003-converge-quick-add-content-path.md.
   *
   * @param {Document} newHtml - The fetched product page.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector("[data-product-grid-content]");
    const modalContent = document.getElementById("quick-add-modal-content");

    if (!productGrid || !modalContent) return;

    productGrid.querySelector(".product-information__media")?.remove();

    morph(modalContent, productGrid);
  }
}

if (!customElements.get("quick-add-component")) {
  customElements.define("quick-add-component", QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  /** @type {AbortController} */
  #abortController = new AbortController();
  /** @type {ResizeObserver | null} */
  #overflowObserver = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #overflowSettleTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    // Close on cart-add *initiation*, not on the cart-update that follows the
    // successful add. The cart drawer here is a third party (Upcart) that opens
    // off the completed add and does so much faster than this dialog's close
    // animation, so closing on cart-update let the drawer slide in over a still-
    // open modal/drawer — the jarring overlap QA flagged (PSD-217). Initiation
    // fires at click, before the network round-trip (see Cart.add), so the close
    // animation runs during the fetch and the surface is gone before Upcart
    // opens. Upcart doesn't open on a failed add, so closing early is safe.
    document.addEventListener(
      ThemeEvents.cartUpdateInitiated,
      this.handleCartAddInitiated,
      { signal: this.#abortController.signal },
    );
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink,
    );
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#dispatchVariantUpdateOnWindow,
    );
    // Tear down the desktop overflow observer once the dialog closes.
    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#disconnectOverflowObserver,
      { signal: this.#abortController.signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#disconnectOverflowObserver();
  }

  showDialog() {
    super.showDialog();

    setTimeout(() => {
      // Dynamically re-load scripts for the newly fetched page content being displayed in the dialog.
      // The media gallery is stripped from the quick-add content (see updateQuickAddModal), so its
      // script is intentionally not loaded here.
      dynamicallyLoadScript(window.Theme.assetSources.variantPickerJs);
      dynamicallyLoadScript(window.Theme.assetSources.quantitySelectorJs);
    });

    // This is needed for the Abra 3rd-party vendor to apply discount pricing
    this.dispatchEvent(
      new QuickAddPopupOpenedEvent({
        dialog: this,
      }),
    );

    // Desktop only: keep the modal scrollbar-free unless the settled content
    // genuinely overflows (see #syncContentOverflow + async.css).
    this.#observeContentOverflow();
  }

  /**
   * The desktop modal content defaults to `overflow-y: hidden` (async.css) so
   * the brief overflow while the morphed content settles on open never flashes
   * a scrollbar. This re-enables `overflow-y: auto` only once the content has
   * SETTLED and still overflows — a tall product on a short window — so the Add
   * to cart button stays reachable. Debounced so a transient overflow during
   * settling doesn't trigger a scrollbar, and it self-corrects (a later resize
   * re-runs the check). Mobile keeps the base `overflow-y: auto` from CSS.
   */
  #syncContentOverflow = () => {
    const content = this.querySelector("#quick-add-modal-content");
    if (!(content instanceof HTMLElement)) return;
    if (!QUICK_ADD_DESKTOP_MEDIA?.matches) {
      content.style.overflowY = ""; // let base/mobile CSS drive it
      return;
    }
    const overflows = content.scrollHeight - content.clientHeight > 1;
    content.style.overflowY = overflows ? "auto" : "hidden";
  };

  #observeContentOverflow() {
    const content = this.querySelector("#quick-add-modal-content");
    if (!(content instanceof HTMLElement)) return;
    if (typeof ResizeObserver === "undefined") {
      // No ResizeObserver: fall back to always-scrollable on desktop so tall
      // content can never be clipped (a brief scrollbar is acceptable
      // degradation in that rare environment). Mobile keeps the base CSS.
      if (QUICK_ADD_DESKTOP_MEDIA?.matches) content.style.overflowY = "auto";
      return;
    }
    this.#disconnectOverflowObserver();
    this.#overflowObserver = new ResizeObserver(() => {
      // Debounce so we read a settled layout, not the transient overflow while
      // the morphed content and its components finish laying out.
      if (this.#overflowSettleTimer) clearTimeout(this.#overflowSettleTimer);
      this.#overflowSettleTimer = setTimeout(this.#syncContentOverflow, 250);
    });
    this.#overflowObserver.observe(content);
    // Also watch the morphed content itself, whose height changes as it settles
    // even while the capped container's box does not.
    const inner = content.firstElementChild;
    if (inner) this.#overflowObserver.observe(inner);
    // Fallback in case no resize fires (content already stable on open).
    if (this.#overflowSettleTimer) clearTimeout(this.#overflowSettleTimer);
    this.#overflowSettleTimer = setTimeout(this.#syncContentOverflow, 250);
  }

  #disconnectOverflowObserver = () => {
    this.#overflowObserver?.disconnect();
    this.#overflowObserver = null;
    if (this.#overflowSettleTimer) {
      clearTimeout(this.#overflowSettleTimer);
      this.#overflowSettleTimer = null;
    }
    const content = this.querySelector("#quick-add-modal-content");
    if (content instanceof HTMLElement) content.style.overflowY = "";
  };

  /**
   * Closes the dialog as soon as an add-to-cart is initiated from within it, so
   * the surface is already gone by the time the (Upcart) cart drawer opens off
   * the completed add. See the listener in connectedCallback.
   * @param {CartAddInitiatedEvent} _event - The cart-add-initiated event
   */
  handleCartAddInitiated = (_event) => {
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector(".view-product-title a")
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector(".view-product-title a")
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
  };

  #dispatchVariantUpdateOnWindow = (/** @type {CustomEvent} */ event) => {
    if (!this.refs.dialog?.open) return;

    requestIdleCallback(() => {
      // This is needed for the Abra 3rd-party vendor to apply discount pricing
      this.dispatchEvent(
        new QuickAddVariantUpdatedEvent({
          dialog: this,
        }),
      );
    });
  };
}

if (!customElements.get("quick-add-dialog")) {
  customElements.define("quick-add-dialog", QuickAddDialog);
}
